import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Gauge, TrendingUp, ChevronDown, Lock, Sparkles } from "lucide-react";
import { PrematchFixture } from "@/types/api";
import { MarketBadge, CompetitionLabel, ResultBadge, TeamMono, RiskChip } from "@/components/badges/Badges";
import { TeamLogo, logoFrom } from "@/components/common/TeamLogo";
import { FollowButton, AddToSlipButton } from "@/components/cards/Actions";
import { MatchProfileStrip, MatchProfileDetail } from "@/components/cards/PrematchMetrics";
import { MarketHubSections, MarketHubCardHint, AdvancedLock } from "@/components/cards/MarketHub";
import { useMarketHubMap } from "@/hooks/useAtlas";
import { useLaunch } from "@/context/LaunchContext";
import { lifecycleOf, resultSurface } from "@/presentation/resultPresentation";
import { lockPrematchPick } from "@/presentation/entitlements";
import { presentOdd } from "@/presentation/oddsPresentation";
import { fmtPct, kickoffLabel } from "@/lib/format";
import { useI18n } from "@/i18n";

export function PrematchCard({ f, index = 0 }: { f: PrematchFixture; index?: number }) {
  const { d, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const { lookup: hubLookup } = useMarketHubMap();
  const hub = hubLookup(f);
  const { hasPlan } = useLaunch();
  const canAdvanced = hasPlan("ELITE");
  // FREE must NOT receive/render an actionable CURRENT prematch recommendation
  // (primary market, odd, confidence, edge, risk, profile metrics, alternatives,
  // watching). Defense-in-depth: the card renders discovery identity only and a
  // locked ATLAS state. Resolved historical cards are unaffected.
  const canPick = hasPlan("PRO");
  const best = f.best || f.markets?.[0] || null;
  // MarketHub.primary (FootyStats-aligned, canonical product source) OVERRIDES
  // the legacy prematch selection for the visible ATLAS pick. Legacy is only a
  // fallback when Market Hub has no primary. Never merge two primaries.
  const hubPrimary = hub?.primary && hub.primary.is_recommendation !== false ? hub.primary : null;
  const pick = hubPrimary
    ? { market_code: hubPrimary.market_code, market_name: hubPrimary.market_name ?? null, model_probability: hubPrimary.probability ?? null, edge: hubPrimary.edge ?? null, reference_odd: hubPrimary.reference_odd ?? null }
    : best;
  const extra = Math.max(0, (f.markets?.length || 0) - 1);
  const key = lifecycleOf(f.status, f.won);
  const resolved = key !== "PENDING";
  const lockPick = lockPrematchPick(canPick, f.status, f.won);
  const odd = pick ? presentOdd({ value: pick.reference_odd, source: null }) : null;
  const label = `${f.home_team} — ${f.away_team}`;
  // Additive FootyStats enrichment (only rendered when genuinely present).
  const fs = f.footystats_metrics || best?.footystats_metrics || null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.3) }}
      className={`panel panel-hover group relative flex flex-col overflow-hidden p-4 ${resolved ? resultSurface(key).card + " pl-5" : ""}`}
      data-testid="prematch-card"
    >
      {resolved && <span className={`absolute inset-y-0 left-0 w-1 ${resultSurface(key).accent}`} aria-hidden />}
      <div className="flex items-start justify-between gap-2">
        <CompetitionLabel country={f.country} league={f.league} leagueLogo={logoFrom(f, "league")} />
        {resolved ? <ResultBadge status={f.status} won={f.won} size="sm" /> : (
          <span className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-semibold text-foreground/80">
            {kickoffLabel(f.fixture_date, lang)}
          </span>
        )}
      </div>

      {/* teams first for scannability */}
      <div className="mt-4 space-y-2">
        <div className="flex items-center gap-2"><TeamLogo src={logoFrom(f, "home")} name={f.home_team} size={26} /><p className="truncate text-sm font-semibold text-foreground">{f.home_team}</p></div>
        <div className="flex items-center gap-2"><TeamLogo src={logoFrom(f, "away")} name={f.away_team} size={26} /><p className="truncate text-sm font-semibold text-foreground">{f.away_team}</p></div>
      </div>

      {/* ATLAS decision block — FREE sees a locked state with NO market metadata */}
      {lockPick ? (
        <Link to="/app/membership" data-testid="prematch-pick-locked" className="mt-4 flex items-center justify-between gap-2 rounded-xl border border-gold/20 bg-gold/[0.06] p-3 transition hover:bg-gold/10">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gold/10 text-gold-300"><Lock className="h-4 w-4" /></span>
            <div className="min-w-0">
              <p className="eyebrow text-gold-300">{lang === "tr" ? "ATLAS analizi hazır" : "ATLAS analysis ready"}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{lang === "tr" ? "Ana seçim PRO ile açılır" : "Primary pick unlocks with PRO"}</p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gradient-to-r from-atlas-500 to-atlas-600 px-3 py-1.5 text-[11px] font-bold text-atlas-ink"><Sparkles className="h-3 w-3" /> PRO</span>
        </Link>
      ) : pick && (
        <div className="mt-4 rounded-xl border border-atlas/20 bg-atlas/[0.05] p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="eyebrow text-atlas-300">{lang === "tr" ? "ATLAS seçimi" : "ATLAS pick"}</p>
              <div className="mt-1.5"><MarketBadge code={pick.market_code} backendLabel={pick.market_name} /></div>
            </div>
            <RiskChip code={pick.market_code} />
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {pick.model_probability != null && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Gauge className="h-3.5 w-3.5 text-atlas-400" /> {d.common.confidence}
                <span className="num font-semibold text-foreground/90">{fmtPct(pick.model_probability)}</span>
              </div>
            )}
            {pick.edge != null && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><TrendingUp className="h-3.5 w-3.5 text-atlas-400" /> Edge <span className="num font-semibold text-foreground/90">{pick.edge.toFixed(1)}</span></div>
            )}
            {odd?.show && <span className="num text-xs text-atlas-300">{d.common.refOdds} {odd.value}</span>}
          </div>
        </div>
      )}

      {/* FootyStats compact match profile (additive; hidden for FREE current picks) */}
      {!resolved && !lockPick && fs && <MatchProfileStrip fs={fs} marketCode={pick?.market_code || ""} lang={lang} />}

      {/* in-card expandable analysis (keeps the detail link separate) */}
      {!resolved && !lockPick && fs && (
        canAdvanced ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="prematch-profile-toggle"
            aria-expanded={open}
            className="mt-2.5 inline-flex items-center gap-1 self-start rounded-full border border-white/8 bg-white/[0.02] px-2.5 py-1 text-[11px] font-semibold text-muted-foreground transition hover:border-atlas/30 hover:text-atlas-300"
          >
            {open ? (lang === "tr" ? "Profili Gizle" : "Hide Profile") : (lang === "tr" ? "Maç Profili" : "Match Profile")}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && <MatchProfileDetail fs={fs} lang={lang} />}
        </>
        ) : (
          <div className="mt-2.5"><AdvancedLock compact /></div>
        )
      )}
      {!lockPick && hub && <MarketHubCardHint fixture={hub} />}
      {!lockPick && open && hub && <MarketHubSections fixture={hub} variant="card" />}

      {/* actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2 pt-1">
        {!lockPick && pick && <AddToSlipButton size="sm" selection={{ fixtureId: f.fixture_id, home: f.home_team, away: f.away_team, country: f.country, league: f.league, marketCode: pick.market_code, marketName: pick.market_name, confidence: pick.model_probability, edge: pick.edge, referenceOdd: pick.reference_odd, kickoff: f.fixture_date }} />}
        <FollowButton fixtureId={f.fixture_id} label={label} league={f.league} size="sm" />
        <Link
          to={`/app/match/${f.fixture_id}`}
          data-testid="prematch-card-detail-link"
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-white/5 bg-white/[0.02] px-3 py-1.5 text-[11px] font-semibold text-muted-foreground transition hover:border-atlas/30 hover:text-atlas-300"
        >
          {!lockPick && extra > 0 ? `+${extra} · ` : ""}{lang === "tr" ? "Analizi Aç" : "Open Analysis"} <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </motion.div>
  );
}
